import { useCallback } from 'react'
import usePresenceChannel, {
  PresencePayload,
  PresenceViewer as Viewer,
} from 'app/frontend/base/hooks/usePresenceChannel'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import VisualPlan from 'app/frontend/types/custom/VisualPlan'

type Options = {
  eventId?: number
  onEventUpdated?: (event: wowaudit.Event) => void
  onLinkedRaidPlanUpdated?: (raidPlan: RaidPlan) => void
  onLinkedVisualPlanUpdated?: (visualPlan: VisualPlan) => void
  onLinkedVisualPlanDeleted?: (visualPlanId: number) => void
}

type EventChannelPayload = PresencePayload & {
  event?: wowaudit.Event
  raid_plan?: RaidPlan
  visual_plan?: VisualPlan
  visual_plan_id?: number
}

const useEventChannel = ({
  eventId,
  onEventUpdated,
  onLinkedRaidPlanUpdated,
  onLinkedVisualPlanUpdated,
  onLinkedVisualPlanDeleted,
}: Options) => {
  const handlePayload = useCallback(
    (payload: EventChannelPayload) => {
      if (payload.type === 'event_updated' && payload.event) {
        onEventUpdated?.(payload.event)
      }
      if (payload.type === 'linked_raid_plan_updated' && payload.raid_plan) {
        onLinkedRaidPlanUpdated?.(payload.raid_plan)
      }
      if (
        payload.type === 'linked_visual_plan_updated' &&
        payload.visual_plan
      ) {
        onLinkedVisualPlanUpdated?.(payload.visual_plan)
      }
      if (
        payload.type === 'linked_visual_plan_deleted' &&
        payload.visual_plan_id !== undefined
      ) {
        onLinkedVisualPlanDeleted?.(payload.visual_plan_id)
      }
    },
    [
      onEventUpdated,
      onLinkedRaidPlanUpdated,
      onLinkedVisualPlanUpdated,
      onLinkedVisualPlanDeleted,
    ],
  )

  const { viewers, requiresReload } = usePresenceChannel<EventChannelPayload>({
    channelName: 'EventChannel',
    channelId: eventId,
    channelIdParam: 'event_id',
    onPayload: handlePayload,
  })

  return {
    viewers,
    requiresReload,
  }
}

export type { Viewer }
export default useEventChannel
